import React from 'react';
import { 
  History, 
  FilePlus, 
  UserCheck, 
  Play, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  MessageSquare,
  Repeat
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const getActionDetails = (action) => {
  switch (action) {
    case 'PRESCRIPTION_CREATED':
      return { icon: FilePlus, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-950', label: 'Prescription Created' };
    case 'ASSIGNED':
      return { icon: UserCheck, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-950', label: 'Prescription Assigned' };
    case 'REASSIGNED':
      return { icon: Repeat, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-950', label: 'Prescription Reassigned' };
    case 'CLAIMED':
      return { icon: UserCheck, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-950', label: 'Claimed by Staff' };
    case 'STARTED':
      return { icon: Play, color: 'text-sky-600', bg: 'bg-sky-100 dark:bg-sky-950', label: 'Processing Started' };
    case 'WAITING':
      return { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-950', label: 'Placed in Waiting' };
    case 'RESUMED':
      return { icon: Play, color: 'text-teal-600', bg: 'bg-teal-100 dark:bg-teal-950', label: 'Processing Resumed' };
    case 'NOTE_ADDED':
      return { icon: MessageSquare, color: 'text-gray-600', bg: 'bg-gray-100 dark:bg-gray-800', label: 'Note Added' };
    case 'COMPLETED':
      return { icon: CheckCircle2, color: 'text-emerald-700', bg: 'bg-emerald-100 dark:bg-emerald-950', label: 'Prescription Completed' };
    case 'CANCELLED':
      return { icon: XCircle, color: 'text-destructive', bg: 'bg-red-100 dark:bg-red-950', label: 'Prescription Cancelled' };
    default:
      return { icon: History, color: 'text-primary', bg: 'bg-primary/10', label: action.replace('_', ' ') };
  }
};

export const PrescriptionActivityTimeline = ({ activities = [] }) => {
  return (
    <Card className="border-border shadow-sm">
      <CardHeader className="pb-3 border-b border-border/50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-serif flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            Audit Activity Timeline
          </CardTitle>
          <Badge variant="outline" className="text-xs font-normal">
            {activities.length} {activities.length === 1 ? 'Event' : 'Events'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        {activities.length === 0 ? (
          <div className="text-center py-6 text-xs text-muted-foreground">
            No activity events recorded yet.
          </div>
        ) : (
          <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border/60">
            {activities.map((act) => {
              const details = getActionDetails(act.action);
              const Icon = details.icon;

              return (
                <div key={act.id} className="relative group">
                  {/* Timeline bullet */}
                  <div className={`absolute -left-6 top-1 h-5 w-5 rounded-full flex items-center justify-center ${details.bg}`}>
                    <Icon className={`h-3 w-3 ${details.color}`} />
                  </div>

                  {/* Content card */}
                  <div className="rounded-lg p-2.5 bg-card border border-border/70 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground flex items-center gap-1.5">
                        {details.label}
                      </span>
                      <span className="text-[11px] text-muted-foreground font-mono">
                        {new Date(act.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-muted-foreground text-[11px]">
                      <span>By: <strong className="text-foreground">{act.actor_name}</strong> ({act.actor_role})</span>
                      {act.previous_status && act.new_status && act.previous_status !== act.new_status && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1">
                          {act.previous_status} → {act.new_status}
                        </Badge>
                      )}
                    </div>

                    {act.metadata_text && (
                      <p className="text-muted-foreground text-xs pt-1 italic border-t border-border/40 mt-1">
                        "{act.metadata_text}"
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PrescriptionActivityTimeline;
